import React, { useState, useEffect, createRef } from 'react';
import { useTranslation } from 'react-i18next';

import ScrollSpy from '../common/ScrollSpy';
import PersonalData from './partials/PersonalData';
import Address from './partials/Address';
import Contacts from './partials/Contacts';
import License from './partials/License';
import Document from './partials/Document';

import useNations from '../useNations';
import { getCities } from '../../utils/functions';
import { validateCustomerForm } from '../../utils/validators';

import { ReactComponent as IconUser } from '../../assets/icon-user.svg';
import { ReactComponent as IconLocation } from '../../assets/icon-location.svg';
import { ReactComponent as IconPhone } from '../../assets/icon-phone.svg';
import { ReactComponent as IconCard } from '../../assets/icon-card.svg';
import { ReactComponent as IconError } from '../../assets/icon-error.svg';

const Form = ({
  formData,
  setFormData,
  invalidFields = {}
}) => {
  const { t } = useTranslation();
  const nations = useNations();

  const [invalidFieldList, setInvalidFieldList] = useState({});
  const [showInvalidFieldsBox, setShowInvalidFieldsBox] = useState(false);

  const [isItalia, setIsItalia] = useState(false);
  const [isDriver, setIsDriver] = useState(true);

  const handleChange = (field, value) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [field]: value
    }));
  };

  const handleBlur = (field) => {
    const { invalidFields } = validateCustomerForm(t, formData);
    if (Object.keys(invalidFields).find(property => property === field)) {
      setInvalidFieldList((prevInvalidFieldList) => ({
        ...prevInvalidFieldList,
        [field]: invalidFields[field]
      }));
    }
    else {
      const { [field]: value, ...rest } = invalidFieldList;
      setInvalidFieldList(rest);
    }
  };

  useEffect(() => {
    setShowInvalidFieldsBox(Object.keys(invalidFields).length > 0);
    setInvalidFieldList(invalidFields);
  }, [invalidFields]);

  useEffect(() => {
    formData.nation && setIsItalia(formData.nation === 'ITALIA');
  }, [formData.nation]);

  useEffect(() => {
    switch (formData.type) {
      case 'driver' || 'individual':
        setIsDriver(true);
        break;
      case 'company':
        setIsDriver(false);
        break;
      default:
        setIsDriver(formData.isDriver);
        break;
    }
  }, [formData.type, formData.isDriver]);

  return <div className='customer'>
    {/* Navigation */}
    <div className='customer__navigation'>
      <ScrollSpy className='item--active' activeClassName='item--active' offsetTop={40}>
        <a className='customer__navigation__item' ref={createRef()} href='.data--personal'>
          <IconUser />
          {['individual', 'driver'].includes(formData.type) ? t('customer.navigation.personal') : t('customer.navigation.company')}
        </a>
        <a className='customer__navigation__item' ref={createRef()} href='.data--address'>
          <IconLocation />
          {t('customer.navigation.address')}
        </a>
        <a className='customer__navigation__item' ref={createRef()} href='.data--contact'>
          <IconPhone />
          {t('customer.navigation.contact')}
        </a>
        {isDriver && (<>
          <a className='customer__navigation__item' ref={createRef()} href='.data--license'>
            <IconCard />
            {t('customer.navigation.license')}
          </a>
          <a className='customer__navigation__item' ref={createRef()} href='.data--document'>
            <IconCard />
            {t('customer.navigation.document')}
          </a>
        </>)}
      </ScrollSpy>
    </div>

    {/* Form */}
    <div className='customer__form'>
      <PersonalData
        formData={formData}
        invalidFields={invalidFieldList}
        handleChange={handleChange}
        handleBlur={handleBlur}

        nations={nations}
        getCities={getCities}
      />
      <Address
        formData={formData}
        invalidFields={invalidFieldList}
        handleChange={handleChange}
        handleBlur={handleBlur}

        isItalia={isItalia}
        nations={nations}
        getCities={getCities}
      />
      <Contacts
        formData={formData}
        invalidFields={invalidFieldList}
        handleChange={handleChange}
        handleBlur={handleBlur}

        isItalia={isItalia}
      />
      <License
        formData={formData}
        invalidFields={invalidFieldList}
        handleChange={handleChange}
        handleBlur={handleBlur}

        isItalia={isItalia}
        isDriver={isDriver}
        nations={nations}
      />
      {isDriver && <Document
        formData={formData}
        invalidFields={invalidFieldList}
        handleChange={handleChange}
        handleBlur={handleBlur}

        isItalia={isItalia}
        nations={nations}
      />}
    </div>

    {/* Alert box */}
    {showInvalidFieldsBox && (<>
      <div className='customer__alert'>
        <button type='button' className='customer__alert__times' onClick={() => setShowInvalidFieldsBox(false)}></button>
        <IconError className='customer__alert__icon' />
        <h2 className='customer__alert__title'>{t('customer.alert.title')}</h2>
        <div className='customer__alert__content'>
          {t('customer.alert.text')}
          <ul className='customer__alert__fieldlist'>
            {Object.keys(invalidFields).map((field, index) => <li key={index} className='customer__alert__field'>
              {t(`customer.form.${field}.label`)}
            </li>)}
          </ul>
        </div>
      </div>
    </>)}
  </div>;
};

export default Form;
